import { Check, X } from "lucide-react";
import { Link } from "react-router-dom";

const PLAN_COLUMNS = [
  {
    key: "basic",
    title: "Digital Agent (Basic)",
    price: "S$129/month",
    billing: "Monthly billing",
    bestFor: "Best for: New/Part-time Agents",
    cta: "/subscribe?plan=basic",
  },
  {
    key: "pro",
    title: "Alpha Investor (Pro)",
    price: "S$249/month",
    billing: "Monthly billing",
    bestFor: "Best for: Individual Top Producers",
    cta: "/subscribe?plan=pro",
    badge: "Best Value",
  },
  {
    key: "elite",
    title: "Elite Team (Enterprise)",
    price: "S$899/month",
    billing: "Monthly billing",
    bestFor: "Best for: Team Leaders / Boutique Bosses",
    cta: "/subscribe?plan=elite",
  },
];

const PRICING_CLARITY_CARDS = [
  { label: "Starter point", value: "S$129/mo", detail: "For agents who need branded visibility and clean listing control." },
  { label: "Most selected", value: "S$249/mo", detail: "The clearest step-up for solo agents who want AI + lead operating tools." },
  { label: "Team scale", value: "S$899/mo", detail: "Built for boutique leaders who need seats, assignment, and team branding." },
  { label: "Billing format", value: "Monthly", detail: "Clear month-to-month pricing with a direct upgrade path as your portal grows." },
];

const FEATURE_ROWS = [
  { label: "Listings", basic: "Up to 50 Manual", pro: "Unlimited + Portfolio Sync", elite: "Unlimited + Portfolio Sync" },
  { label: "Branding", basic: "Standard Portal", pro: "Personal Subdomain", elite: "Team-Wide Subdomain Branding" },
  { label: "Investment Tools", basic: "Basic Tax Panel", pro: "Full Alpha Formula + Cockpit", elite: "Full Alpha Formula + Cockpit" },
  { label: "Lead Tracking", basic: "Name/Email Only", pro: "Sales Command Center", elite: "Sales Command Center" },
  { label: "Lead Alerts", basic: "Standard Email", pro: "Real-time Alerts", elite: "Real-time HOT LEAD Alerts" },
  { label: "Marketing", basic: false, pro: "AI Staging + Exit Memo PDFs", elite: "AI Staging + Exit Memo PDFs" },
  { label: "Special Features", basic: false, pro: "Price-Gap Detector", elite: "District Triage + Price-Gap Detector" },
  { label: "Team Tools", basic: false, pro: false, elite: "Lead Assignment + Seats (5)" },
  { label: "Compliance", basic: "2026 PMLPFTF Tracker", pro: "2026 PMLPFTF Tracker", elite: "2026 PMLPFTF Tracker" },
];

const renderValue = (value) => {
  if (value === false) {
    return <span className="inline-flex items-center gap-2 text-slate-400"><X className="h-4 w-4" /> Not included</span>;
  }
  return <span className="inline-flex items-start gap-2 text-[#001935]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" /> <span>{value}</span></span>;
};

export const SubscriptionPlansTable = () => {
  return (
    <section className="rounded-[36px] border border-[#001935]/10 bg-[#FFFAF1] p-6 shadow-[0_20px_70px_rgba(0,25,53,0.08)] lg:p-8" data-testid="subscription-plans-section" id="subscription-plans-section">
      <div className="max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Subscription Plans</p>
        <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-[#001935]" data-testid="subscription-plans-heading">Choose SG Prop Portal subscription plan</h2>
        <p className="mt-4 text-sm leading-8 text-[#42566f]" data-testid="subscription-plans-description">A side-by-side view of every SG Prop Portal tier — from starter visibility to full Alpha Intelligence for boutique teams.</p>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="subscription-pricing-clarity-grid">
        {PRICING_CLARITY_CARDS.map((card) => (
          <div className="rounded-[24px] border border-[#001935]/10 bg-white px-5 py-5" data-testid={`subscription-pricing-clarity-card-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={card.label}>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">{card.label}</p>
            <p className="mt-3 text-2xl font-semibold text-[#001935]">{card.value}</p>
            <p className="mt-3 text-sm leading-7 text-[#42566f]">{card.detail}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 hidden overflow-hidden rounded-[30px] border border-[#001935]/10 lg:block" data-testid="subscription-plans-table-desktop">
        <div className="grid grid-cols-[1.15fr_repeat(3,minmax(0,1fr))]">
          <div className="bg-[#001935] px-5 py-6 text-sm font-bold uppercase tracking-[0.16em] text-[#D4AF37]" data-testid="subscription-plans-header-feature">Feature</div>
          {PLAN_COLUMNS.map((plan) => (
            <div className={`relative border-l border-[#FFFAF1]/8 bg-[#001935] px-5 py-6 text-[#D4AF37] ${plan.badge ? "ring-2 ring-[#D4AF37] ring-inset" : ""}`} data-testid={`subscription-plans-header-${plan.key}`} key={plan.key}>
              {plan.badge ? <span className="absolute right-5 top-4 rounded-full bg-[#D4AF37] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#001935]" data-testid="subscription-plans-best-value-badge">Best Value</span> : null}
              <p className="pr-24 font-[Cormorant_Garamond,serif] text-3xl font-bold">{plan.title}</p>
              <p className="mt-4 text-xl font-semibold">{plan.price}</p>
              <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#D4AF37]/76">{plan.billing}</p>
              <p className="mt-3 text-sm leading-7 text-[#D4AF37]/82">{plan.bestFor}</p>
            </div>
          ))}

          {FEATURE_ROWS.map((row) => (
            <div className="contents" key={`${row.label}-row`}>
              <div className="border-t border-[#001935]/10 bg-[#fffaf1] px-5 py-4 font-semibold text-[#001935]" data-testid={`subscription-feature-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={`${row.label}-label`}>
                {row.label}
              </div>
              {PLAN_COLUMNS.map((plan) => (
                <div className={`border-l border-t border-[#001935]/10 bg-white px-5 py-4 text-sm leading-7 ${plan.badge ? "border-x-2 border-[#D4AF37]" : ""}`} data-testid={`subscription-cell-${plan.key}-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={`${plan.key}-${row.label}`}>
                  {renderValue(row[plan.key])}
                </div>
              ))}
            </div>
          ))}

          <div className="border-t border-[#001935]/10 bg-[#fffaf1] px-5 py-5 text-sm font-semibold text-[#42566f]">Get Started</div>
          {PLAN_COLUMNS.map((plan) => (
            <div className={`border-l border-t border-[#001935]/10 bg-white px-5 py-5 ${plan.badge ? "border-x-2 border-[#D4AF37]" : ""}`} key={`${plan.key}-cta`}>
              <Link className="gold-button !w-full !justify-center" data-testid={`subscription-plans-cta-${plan.key}`} to={plan.cta}>
                Subscribe Now
              </Link>
              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#42566f]" data-testid={`subscription-plans-cta-note-${plan.key}`}>Hosted signup flow • direct plan selection</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-5 lg:hidden" data-testid="subscription-plans-table-mobile">
        {PLAN_COLUMNS.map((plan) => (
          <div className={`rounded-[30px] border bg-white p-5 ${plan.badge ? "border-2 border-[#D4AF37]" : "border-[#001935]/10"}`} data-testid={`subscription-mobile-card-${plan.key}`} key={plan.key}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-[Cormorant_Garamond,serif] text-3xl font-bold text-[#001935]">{plan.title}</p>
                <p className="mt-2 text-xl font-semibold text-[#001935]">{plan.price}</p>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">{plan.billing}</p>
                <p className="mt-2 text-sm leading-7 text-[#42566f]">{plan.bestFor}</p>
              </div>
              {plan.badge ? <span className="rounded-full bg-[#D4AF37] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#001935]">Best Value</span> : null}
            </div>
            <div className="mt-5 space-y-3 text-sm leading-7 text-[#001935]">
              {FEATURE_ROWS.map((row) => (
                <div className="rounded-2xl border border-[#001935]/8 bg-[#FFFAF1] px-4 py-3" key={`${plan.key}-${row.label}-mobile`}>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]">{row.label}</p>
                  <div className="mt-2">{renderValue(row[plan.key])}</div>
                </div>
              ))}
            </div>
            <Link className="gold-button mt-5 !w-full !justify-center" data-testid={`subscription-mobile-cta-${plan.key}`} to={plan.cta}>
              Subscribe Now
            </Link>
            <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#42566f]">Hosted signup flow • direct plan selection</p>
          </div>
        ))}
      </div>

      <p className="mt-6 text-sm font-semibold text-[#42566f]" data-testid="subscription-plans-compliance-note">Compliance: All tiers include the 2026 PMLPFTF Tracker.</p>
    </section>
  );
};